import { UniButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, H6 } from '@/design-system/components/typography';
import { SxProp, View } from 'dripsy';
import _, { isString } from 'lodash';
import React, { useContext, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import { BottomSheetHeader } from '@/design-system/components/headers';
import BottomSheet, { useBottomSheetDynamicSnapPoints, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { CancelCardStackParamList, CancelCardStackRoutes } from './type';
import { Card } from '../../../../../../packages/utx-access-client/src/react';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { CardCancelReasonFilters } from '../../options';
import { errorParser } from '@/features/bill-payment/screens/otp/type';

export interface CancelCardProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  data: any;
  account: string;
  tab?: number;
  record?: Card;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
}

export const CancelCardNativeOneScreen: React.FC<CancelCardProps> = (props) => {
  const { onClose, onLayout, data, account, tab, record, func, onReturnData, refresh } = props;
  const { t } = useTranslation('account-management');
  const { body, textMb, textTitle, textAtmPin, btnView, btn, modal, topHeader } = sxStyles;

  const layout = useContext(LayoutContext);
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const navigation = useNavigation<NativeStackNavigationProp<CancelCardStackParamList>>();

  const [alertMessage, setAlertMessage] = useState('');
  const [loading, setLoading] = useState(false);

  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet
        enablePanDownToClose
        index={1}
        snapPoints={animatedSnapPoints}
        handleHeight={animatedHandleHeight}
        contentHeight={animatedContentHeight}
        backdropComponent={renderBackdrop}
        handleComponent={() => <View />}>
        <View sx={modal}>
          <View sx={{ minHeight: 275 + (alertMessage ? 60 : 0), backgroundColor: '$backgroundPrimary' }} onLayout={handleContentLayout}>
            <View sx={topHeader}>
              <BottomSheetHeader
                title={t('credit-card-cancel-card::cancel-card-confirmation.title--cancel-card-confirmation')}
                onPress={() => navigation.popToTop()}
              />
            </View>
            <View sx={body}>
              <TextBody sx={textMb} semiBold>
                {t('credit-card-cancel-card::cancel-card-confirmation.text--are-you-sure')}
              </TextBody>
              <View>
                <TextCaption sx={textTitle} semiBold>
                  {t('credit-card-cancel-card::cancel-card-form.text--please-select-reason')}
                </TextCaption>
                <H6 sx={textAtmPin}>{CardCancelReasonFilters(t).find((el) => el.value === data.reason)?.label}</H6>
              </View>
              {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={{ mt: 5 }} />}
              <View sx={btnView}>
                <UniButton
                  sx={btn}
                  labelSx={{ color: '$balck' }}
                  label={t('card-account-management::general.button--place-instruction')}
                  loading={loading}
                  disabled={loading}
                  onPress={() => {
                    setLoading(true);
                    func(
                      data,
                      (returnData) => {
                        setLoading(false);
                        onReturnData(returnData.data);
                        navigation.navigate(CancelCardStackRoutes.Placed, {
                          onLayout: onLayout,
                          data,
                          account,
                          tab,
                          returnData: returnData.data,
                          refresh
                        });
                      },
                      (error) => {
                        setLoading(false);
                        const data: any = error.response?.data;
                        const errorMessage =
                          data && isString(data.message?.code) ? errorParser(data.message?.code, t) : errorParser(error.message, t);
                        setAlertMessage(errorMessage);
                      }
                    );
                  }}
                />
              </View>
            </View>
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    width: '100%',
    padding: '$4'
  },
  body: {
    gap: '$5'
  },
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$4'
  },
  btn: {
    width: '100%',
    height: 44
  }
};
